import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useEgresos } from '../hooks/useEgresos';
import { LoggerService } from '../../../shared/services/logger.service';
import { SmartPDFUploader } from '../../../components/debug/SmartPDFUploader';
type TipoEgreso = 'alimentacion' | 'transporte' | 'vivienda' | 'salud' | 'educacion' | 'entretenimiento' | 'ropa' | 'servicios' | 'otros';

interface EgresoFormData {
  concepto: string;
  categoria: TipoEgreso | '';
  monto: number;
  fecha: string;
  descripcion?: string;
}

interface EgresoMetrics {
  totalMensual: number;
  gastoPromedioDiario: number;
  categoriasPrincipales: number;
  variacionMensual: number;
}

export const ModernEgresosDashboard: React.FC = () => {
  const { egresos, loading, error, createEgreso, deleteEgreso } = useEgresos();
  const [searchParams] = useSearchParams();
  const [showForm, setShowForm] = useState(false);
  const [categoriaFiltro, setCategoriaFiltro] = useState<string>('');
  const [searchText, setSearchText] = useState<string>('');
  const [minMonto, setMinMonto] = useState<string>('');
  const [maxMonto, setMaxMonto] = useState<string>('');
  const now = new Date();
  const [mesSeleccionado, setMesSeleccionado] = useState(now.getMonth());
  const [añoSeleccionado, setAñoSeleccionado] = useState(now.getFullYear());
  const [formData, setFormData] = useState<EgresoFormData>({
    concepto: '',
    categoria: '',
    monto: 0,
    fecha: new Date().toISOString().split('T')[0],
    descripcion: ''
  });

  // Detectar filtro de categoría desde URL
  useEffect(() => {
    const categoria = searchParams.get('categoria');
    if (categoria) {
      setCategoriaFiltro(categoria);
    }
  }, [searchParams]);

  // Funciones de navegación mensual
  const cambiarMes = (direccion: 'anterior' | 'siguiente') => {
    if (direccion === 'anterior') {
      if (mesSeleccionado === 0) {
        setMesSeleccionado(11);
        setAñoSeleccionado(añoSeleccionado - 1);
      } else {
        setMesSeleccionado(mesSeleccionado - 1);
      }
    } else {
      if (mesSeleccionado === 11) {
        setMesSeleccionado(0);
        setAñoSeleccionado(añoSeleccionado + 1);
      } else {
        setMesSeleccionado(mesSeleccionado + 1);
      }
    }
  };

  const volverMesActual = () => {
    setMesSeleccionado(now.getMonth());
    setAñoSeleccionado(now.getFullYear());
  };

  const nombreMes = new Date(añoSeleccionado, mesSeleccionado).toLocaleDateString('es-AR', { 
    month: 'long', 
    year: 'numeric' 
  });

  // Filtrar y paginar egresos
  const egresosFiltrados = egresos.filter(egreso => {
    const egresoDate = new Date(egreso.fecha);
    const matchMes = egresoDate.getMonth() === mesSeleccionado && egresoDate.getFullYear() === añoSeleccionado;
    const matchCategoria = !categoriaFiltro || egreso.categoria.toLowerCase() === categoriaFiltro.toLowerCase();
    const matchSearch = !searchText || 
      egreso.concepto?.toLowerCase().includes(searchText.toLowerCase()) ||
      egreso.descripcion?.toLowerCase().includes(searchText.toLowerCase());
    const matchMinMonto = !minMonto || egreso.monto >= parseFloat(minMonto);
    const matchMaxMonto = !maxMonto || egreso.monto <= parseFloat(maxMonto);
    
    return matchMes && matchCategoria && matchSearch && matchMinMonto && matchMaxMonto;
  });

  const limpiarFiltro = () => {
    setCategoriaFiltro('');
    setSearchText('');
    setMinMonto('');
    setMaxMonto('');
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('es-AR', {
      style: 'currency',
      currency: 'ARS',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatDate = (date: Date) => {
    return new Intl.DateTimeFormat('es-AR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    }).format(date);
  };

  const getMetrics = (): EgresoMetrics => {
    const currentMonth = new Date().getMonth();
    const currentYear = new Date().getFullYear();
    
    // Egresos del mes actual
    const egresosDelMes = egresos.filter(egreso => {
      const egresoDate = new Date(egreso.fecha);
      return egresoDate.getMonth() === currentMonth && egresoDate.getFullYear() === currentYear;
    });

    const totalMensual = egresosDelMes.reduce((total, egreso) => total + egreso.monto, 0);
    const gastoPromedioDiario = totalMensual / new Date().getDate();
    
    // Categorías únicas del mes
    const categoriasUnicas = new Set(egresosDelMes.map(egreso => egreso.categoria));
    const categoriasPrincipales = categoriasUnicas.size;

    // Variación mensual (simulada)
    const variacionMensual = -5.2; // En una implementación real se calcularía vs mes anterior
    
    return {
      totalMensual,
      gastoPromedioDiario,
      categoriasPrincipales,
      variacionMensual
    };
  };

  const getEgresoIcon = (categoria: TipoEgreso) => {
    const icons: { [key in TipoEgreso]: string } = {
      'alimentacion': 'ri-restaurant-2-line',
      'transporte': 'ri-roadster-line',
      'vivienda': 'ri-home-4-line',
      'salud': 'ri-health-book-line',
      'educacion': 'ri-graduation-cap-line',
      'entretenimiento': 'ri-game-line',
      'ropa': 'ri-shirt-line',
      'servicios': 'ri-tools-line',
      'otros': 'ri-more-2-line'
    };
    return icons[categoria] || 'ri-money-dollar-circle-line';
  };

  const getEgresoColor = (categoria: TipoEgreso) => {
    const colors: { [key in TipoEgreso]: { bg: string; text: string; icon: string } } = {
      'alimentacion': { bg: 'bg-orange-50', text: 'text-orange-700', icon: 'text-orange-600' },
      'transporte': { bg: 'bg-blue-50', text: 'text-blue-700', icon: 'text-blue-600' },
      'vivienda': { bg: 'bg-green-50', text: 'text-green-700', icon: 'text-green-600' },
      'salud': { bg: 'bg-red-50', text: 'text-red-700', icon: 'text-red-600' },
      'educacion': { bg: 'bg-purple-50', text: 'text-purple-700', icon: 'text-purple-600' },
      'entretenimiento': { bg: 'bg-pink-50', text: 'text-pink-700', icon: 'text-pink-600' },
      'ropa': { bg: 'bg-indigo-50', text: 'text-indigo-700', icon: 'text-indigo-600' },
      'servicios': { bg: 'bg-yellow-50', text: 'text-yellow-700', icon: 'text-yellow-600' },
      'otros': { bg: 'bg-gray-50', text: 'text-gray-700', icon: 'text-gray-600' }
    };
    return colors[categoria] || colors['otros'];
  };

  const getCategoryDescription = (categoria: TipoEgreso) => {
    const descriptions: { [key in TipoEgreso]: string } = {
      'alimentacion': 'Supermercado, restaurantes, comida',
      'transporte': 'Combustible, transporte público, taxis',
      'vivienda': 'Alquiler, hipoteca, servicios del hogar',
      'salud': 'Médicos, medicamentos, tratamientos',
      'educacion': 'Cursos, libros, materiales educativos',
      'entretenimiento': 'Cine, juegos, actividades recreativas',
      'ropa': 'Vestimenta, calzado, accesorios',
      'servicios': 'Luz, gas, internet, teléfono',
      'otros': 'Gastos varios y misceláneos'
    };
    return descriptions[categoria] || descriptions['otros'];
  };

  const getCategorySummary = () => {
    const currentMonth = new Date().getMonth();
    const currentYear = new Date().getFullYear();
    
    const egresosDelMes = egresos.filter(egreso => {
      const egresoDate = new Date(egreso.fecha);
      return egresoDate.getMonth() === currentMonth && egresoDate.getFullYear() === currentYear;
    });

    const categorias = egresosDelMes.reduce((acc, egreso) => {
      const categoria = egreso.categoria as TipoEgreso;
      if (!acc[categoria]) {
        acc[categoria] = {
          total: 0,
          count: 0,
          categoria
        };
      }
      acc[categoria].total += egreso.monto;
      acc[categoria].count += 1;
      return acc;
    }, {} as Record<TipoEgreso, { total: number; count: number; categoria: TipoEgreso }>);

    return Object.values(categorias).sort((a, b) => b.total - a.total);
  };

  const handleInputChange = (field: keyof EgresoFormData, value: string | number) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = async () => {
    try {
      LoggerService.info('Creating new egreso', 'ModernEgresosDashboard', { formData });
      await createEgreso({
        concepto: formData.concepto,
        categoria: formData.categoria as string,
        monto: formData.monto,
        fecha: new Date(formData.fecha),
        descripcion: formData.descripcion,
        userId: 'current-user' // TODO: Get from auth context
      });
      
      // Reset form
      setFormData({
        concepto: '',
        categoria: '',
        monto: 0,
        fecha: new Date().toISOString().split('T')[0],
        descripcion: ''
      });
      setShowForm(false);
      
      LoggerService.info('Egreso created successfully', 'ModernEgresosDashboard');
    } catch (error) {
      LoggerService.error('Failed to create egreso', 'ModernEgresosDashboard', { error });
    }
  };

  const handleDelete = async (id: string) => {
    try {
      LoggerService.info('Deleting egreso', 'ModernEgresosDashboard', { id });
      await deleteEgreso(id);
      LoggerService.info('Egreso deleted successfully', 'ModernEgresosDashboard');
    } catch (error) {
      LoggerService.error('Failed to delete egreso', 'ModernEgresosDashboard', { error });
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-red-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6 bg-red-50 border border-red-200 rounded-lg">
        <p className="text-red-800">Error: {error}</p>
      </div>
    );
  }

  const metrics = getMetrics();
  const categorySummary = getCategorySummary();

  return (
    <div className="max-w-7xl mx-auto p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Egresos</h1>
          <p className="text-gray-600 mt-1">Controla y gestiona tus gastos mensuales</p>
        </div>
        
        <button
          onClick={() => setShowForm(true)}
          className="flex items-center space-x-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors shadow-md"
        >
          <i className="ri-add-line"></i>
          <span>Nuevo Gasto</span>
        </button>
      </div>

      {/* Metrics Cards */}
      <div className="grid md:grid-cols-4 gap-6">
        {/* Total Mensual */}
        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Total Mensual</p>
              <p className="text-2xl font-bold text-red-600 mt-1">
                {formatCurrency(metrics.totalMensual)}
              </p>
            </div>
            <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center">
              <i className="ri-money-dollar-circle-line text-red-600 text-xl"></i>
            </div>
          </div>
        </div>

        {/* Gasto Promedio Diario */}
        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Promedio Diario</p>
              <p className="text-2xl font-bold text-orange-600 mt-1">
                {formatCurrency(metrics.gastoPromedioDiario)}
              </p>
            </div>
            <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center">
              <i className="ri-calendar-line text-orange-600 text-xl"></i>
            </div>
          </div>
        </div>

        {/* Categorías Activas */}
        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Categorías Activas</p>
              <p className="text-2xl font-bold text-blue-600 mt-1">
                {metrics.categoriasPrincipales}
              </p>
            </div>
            <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
              <i className="ri-pie-chart-line text-blue-600 text-xl"></i>
            </div>
          </div>
        </div>

        {/* Variación Mensual */}
        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Variación Mensual</p>
              <p className={`text-2xl font-bold mt-1 ${metrics.variacionMensual < 0 ? 'text-green-600' : 'text-red-600'}`}>
                {metrics.variacionMensual > 0 ? '+' : ''}{metrics.variacionMensual}%
              </p>
            </div>
            <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${metrics.variacionMensual < 0 ? 'bg-green-100' : 'bg-red-100'}`}>
              <i className={`${metrics.variacionMensual < 0 ? 'ri-arrow-down-line' : 'ri-arrow-up-line'} text-xl ${metrics.variacionMensual < 0 ? 'text-green-600' : 'text-red-600'}`}></i>
            </div>
          </div>
        </div>
      </div>

      {/* Resumen por Categorías */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200">
        <div className="p-6 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">Gastos por Categoría - {new Date().toLocaleDateString('es-AR', { month: 'long', year: 'numeric' })}</h2>
        </div>
        
        <div className="p-6">
          {categorySummary.length === 0 ? (
            <div className="text-center py-12">
              <i className="ri-pie-chart-line text-4xl text-gray-400 mb-4"></i>
              <p className="text-gray-600">No hay gastos registrados este mes</p>
              <p className="text-sm text-gray-500 mt-1">Agrega tu primer gasto para comenzar el seguimiento</p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {categorySummary.map((categoria) => {
                const colorScheme = getEgresoColor(categoria.categoria);
                const icon = getEgresoIcon(categoria.categoria);
                
                return (
                  <div
                    key={categoria.categoria}
                    className={`${colorScheme.bg} rounded-xl p-5 border-2 border-l-4 ${colorScheme.text.replace('text-', 'border-l-')} hover:shadow-lg transition-all duration-300 cursor-pointer`}
                    onClick={() => setCategoriaFiltro(categoria.categoria)}
                  >
                    <div className="text-center">
                      <div className={`w-14 h-14 ${colorScheme.bg} rounded-full flex items-center justify-center mx-auto mb-3 border-2 ${colorScheme.text.replace('text-', 'border-')}`}>
                        <i className={`${icon} ${colorScheme.icon} text-2xl`}></i>
                      </div>
                      
                      <h3 className={`font-bold text-lg ${colorScheme.text} capitalize mb-2`}>
                        {categoria.categoria.replace('_', ' ')}
                      </h3>
                      
                      <p className="text-xs text-gray-500 mb-3 px-2 leading-relaxed">
                        {getCategoryDescription(categoria.categoria)}
                      </p>
                      
                      <div className="border-t pt-3 mt-3">
                        <p className="text-xl font-bold text-gray-900 mb-1">
                          {formatCurrency(categoria.total)}
                        </p>
                        <p className="text-xs text-gray-400">
                          {categoria.count} gasto{categoria.count !== 1 ? 's' : ''} registrado{categoria.count !== 1 ? 's' : ''}
                        </p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Navegación Mensual y Filtros */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 space-y-4">
        {/* Navegación de Mes */}
        <div className="flex items-center justify-between">
          <button
            onClick={() => cambiarMes('anterior')}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            title="Mes anterior"
          >
            <i className="ri-arrow-left-s-line text-xl"></i>
          </button>
          
          <div className="text-center">
            <h3 className="text-lg font-semibold text-gray-900 capitalize">{nombreMes}</h3>
            <p className="text-sm text-gray-600">
              {egresosFiltrados.length} gasto{egresosFiltrados.length !== 1 ? 's' : ''}
            </p>
          </div>
          
          <button
            onClick={() => cambiarMes('siguiente')}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            title="Mes siguiente"
          >
            <i className="ri-arrow-right-s-line text-xl"></i>
          </button>
        </div>

        {(mesSeleccionado !== now.getMonth() || añoSeleccionado !== now.getFullYear()) && (
          <button
            onClick={volverMesActual}
            className="w-full px-4 py-2 text-sm bg-red-50 text-red-700 rounded-lg hover:bg-red-100 transition-colors"
          >
            <i className="ri-calendar-todo-line mr-2"></i>
            Volver al mes actual
          </button>
        )}

        {/* Filtros */}
        <div className="border-t pt-4 space-y-3">
          <h4 className="font-medium text-gray-900 mb-3">Filtros</h4>
          
          {/* Buscador */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Buscar por concepto o descripción
            </label>
            <input
              type="text"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              placeholder="Buscar..."
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
            />
          </div>

          {/* Filtro por categoría */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Categoría
            </label>
            <select
              value={categoriaFiltro}
              onChange={(e) => setCategoriaFiltro(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
            >
              <option value="">Todas las categorías</option>
              <option value="alimentacion">Alimentación</option>
              <option value="transporte">Transporte</option>
              <option value="vivienda">Vivienda</option>
              <option value="salud">Salud</option>
              <option value="educacion">Educación</option>
              <option value="entretenimiento">Entretenimiento</option>
              <option value="ropa">Ropa</option>
              <option value="servicios">Servicios</option>
              <option value="otros">Otros</option>
            </select>
          </div>

          {/* Filtro por rango de monto */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Monto mínimo
              </label>
              <input
                type="number"
                value={minMonto}
                onChange={(e) => setMinMonto(e.target.value)}
                placeholder="$ 0"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Monto máximo
              </label>
              <input
                type="number"
                value={maxMonto}
                onChange={(e) => setMaxMonto(e.target.value)}
                placeholder="$ ∞"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
              />
            </div>
          </div>

          {/* Botón limpiar filtros */}
          {(categoriaFiltro || searchText || minMonto || maxMonto) && (
            <button
              onClick={limpiarFiltro}
              className="w-full px-4 py-2 text-sm bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
            >
              <i className="ri-close-circle-line mr-2"></i>
              Limpiar filtros
            </button>
          )}
        </div>
      </div>

      {/* Lista de Egresos */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200">
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">
                {categoriaFiltro ? `Gastos en ${categoriaFiltro}` : 'Gastos Recientes'}
              </h2>
              {categoriaFiltro && (
                <p className="text-sm text-gray-500 mt-1">
                  {egresosFiltrados.length} gasto{egresosFiltrados.length !== 1 ? 's' : ''} encontrado{egresosFiltrados.length !== 1 ? 's' : ''}
                </p>
              )}
            </div>
            {categoriaFiltro && (
              <button
                onClick={limpiarFiltro}
                className="px-3 py-1 text-sm bg-gray-100 text-gray-600 rounded-lg hover:bg-gray-200 transition-colors"
              >
                <i className="ri-close-line mr-1"></i>
                Limpiar filtro
              </button>
            )}
          </div>
        </div>
        
        <div className="p-6">
          {egresosFiltrados.length === 0 ? (
            <div className="text-center py-12">
              <i className="ri-wallet-line text-4xl text-gray-400 mb-4"></i>
              <p className="text-gray-600">
                {categoriaFiltro ? `No hay gastos en la categoría "${categoriaFiltro}"` : 'No hay gastos registrados'}
              </p>
              <p className="text-sm text-gray-500 mt-1">
                {categoriaFiltro ? 'Prueba con otra categoría' : 'Comienza a rastrear tus gastos'}
              </p>
            </div>
          ) : (
            <>
              <div className="space-y-4">
                {egresosFiltrados.map((egreso) => {
                const colorScheme = getEgresoColor(egreso.categoria as TipoEgreso);
                const icon = getEgresoIcon(egreso.categoria as TipoEgreso);
                
                return (
                  <div
                    key={egreso.id}
                    className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:shadow-md transition-shadow"
                  >
                    <div className="flex items-center space-x-4">
                      <div className={`w-12 h-12 ${colorScheme.bg} rounded-lg flex items-center justify-center border`}>
                        <i className={`${icon} ${colorScheme.icon} text-xl`}></i>
                      </div>
                      
                      <div>
                        <h3 className="font-semibold text-gray-900">{egreso.concepto}</h3>
                        <p className={`text-sm ${colorScheme.text} capitalize`}>
                          {egreso.categoria.replace('_', ' ')} • {formatDate(new Date(egreso.fecha))}
                        </p>
                      </div>
                    </div>
                    
                    <div className="flex items-center space-x-4">
                      <div className="text-right">
                        <p className="font-semibold text-gray-900">
                          -{formatCurrency(egreso.monto)}
                        </p>
                      </div>
                      
                      <button
                        onClick={() => handleDelete(egreso.id)}
                        className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                        title="Eliminar gasto"
                      >
                        <i className="ri-delete-bin-line"></i>
                      </button>
                    </div>
                  </div>
                );
              })}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl p-6 w-full max-w-md space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold">Nuevo Gasto</h3>
              <button
                onClick={() => setShowForm(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <i className="ri-close-line text-xl"></i>
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Concepto
                </label>
                <input
                  type="text"
                  value={formData.concepto}
                  onChange={(e) => handleInputChange('concepto', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
                  placeholder="Ej: Supermercado"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Categoría
                </label>
                <select
                  value={formData.categoria}
                  onChange={(e) => handleInputChange('categoria', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
                >
                  <option value="">Seleccionar categoría</option>
                  <option value="alimentacion">Alimentación</option>
                  <option value="transporte">Transporte</option>
                  <option value="vivienda">Vivienda</option>
                  <option value="salud">Salud</option>
                  <option value="educacion">Educación</option>
                  <option value="entretenimiento">Entretenimiento</option>
                  <option value="ropa">Ropa</option>
                  <option value="servicios">Servicios</option>
                  <option value="otros">Otros</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Monto
                </label>
                <input
                  type="number"
                  value={formData.monto}
                  onChange={(e) => handleInputChange('monto', parseFloat(e.target.value) || 0)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
                  placeholder="0"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Fecha
                </label>
                <input
                  type="date"
                  value={formData.fecha}
                  onChange={(e) => handleInputChange('fecha', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Descripción (opcional)
                </label>
                <textarea
                  value={formData.descripcion}
                  onChange={(e) => handleInputChange('descripcion', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
                  rows={2}
                  placeholder="Información adicional..."
                />
              </div>
            </div>

            <div className="flex justify-end space-x-3 pt-4">
              <button
                onClick={() => setShowForm(false)}
                className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={handleSubmit}
                disabled={!formData.concepto || !formData.categoria || formData.monto <= 0}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Guardar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Sección de procesamiento de PDFs */}
      <div className="mt-8">
        <SmartPDFUploader />
      </div>
    </div>
  );
};